import { faTrash } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  Checkbox,
  Gender,
  JoonUIColor,
  ModalHeader,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import DeleteChildModal from "./DeleteChildModal"
import useDeleteChildModalStore from "./useDeleteChildModalStore"
import useEditChildProfileModalStore from "./useEditChildProfileModalStore"
import DateInput from "../../../components/dateInput/DateInput"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import { GENDER_OPTIONS } from "../../../constants"
import { useEditChildMutation } from "../../../networking/families/mutations"
import { isValidDate } from "../../../util/util"

const GENDER_LABEL_KEYS = {
  [Gender.FEMALE]: "GENDER_GIRL",
  [Gender.MALE]: "GENDER_BOY",
  [Gender.OTHER]: "GENDER_PREFER_NOT",
} as const

const EditChildProfileModal = () => {
  const {
    isOpen,
    onClose,
    name,
    setName,
    birthdate,
    setBirthdate,
    gender,
    setGender,
    child,
  } = useEditChildProfileModalStore()

  const [error, setError] = useState("")
  const { onOpen: openDeleteChildModal } = useDeleteChildModalStore()
  const { t } = useTranslation()

  const editChildMutation = useEditChildMutation()

  const handleSaveChanges = async () => {
    if (!child) return

    if (birthdate && !isValidDate(birthdate)) {
      return setError(t("ECP_ERR_INVALID_BIRTHDATE"))
    }
    if (!name) {
      return setError(t("ECP_ERR_NAME_REQUIRED"))
    }
    setError("")

    await editChildMutation.mutateAsync({
      childInfo: {
        name,
        birthdate: birthdate
          ? dayjs(birthdate).format("YYYY-MM-DD")
          : undefined,
        gender: gender,
      },
      childId: child.id,
    })
    onClose()
  }

  const handleDeleteChild = () => {
    if (!child?.id) return
    openDeleteChildModal(child.id)
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      size="fit-content"
      backgroundType="overlay"
    >
      <ModalHeader title={t("ECP_TITLE")} onClose={onClose} showBorderBottom />
      <div className="flex flex-col gap-4 p-6 pt-4 overflow-y-auto">
        <div className="flex flex-col gap-2">
          <Typography variant="body">{t("CST_NAME")}</Typography>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("ECP_ENTER_NAME")}
            name="name"
            fullWidth
          />
        </div>
        <div className="flex flex-col gap-2">
          <Typography variant="body">{t("CST_GENDER")}</Typography>
          <div className="flex flex-col border border-borderPrimary rounded-md">
            {GENDER_OPTIONS.map((genderOption) => (
              <div className="border-b border-borderPrimary last:border-none p-3">
                <button
                  onClick={() => setGender(genderOption.value)}
                  className="w-full flex flex-row items-center gap-2 justify-between"
                >
                  <Typography variant="body">
                    {t(GENDER_LABEL_KEYS[genderOption.value])}
                  </Typography>
                  <Checkbox
                    selected={gender === genderOption.value}
                    name="gender"
                    hideBorder
                    inputType="radio"
                    onChange={() => setGender(genderOption.value)}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Typography variant="body">{t("ECP_BIRTHDATE_OPTIONAL")}</Typography>
          <DateInput
            value={birthdate || ""}
            onChange={(e) => setBirthdate(e)}
            name="birthdate"
            placeholder={t("ECP_DATE_PLACEHOLDER")}
            fullWidth
          />
        </div>
      </div>
      <div className="flex flex-col justify-end gap-6 p-6 pt-4 border-t border-borderPrimary">
        {error && (
          <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
            {error}
          </Typography>
        )}
        <Button
          text={t("EPM_SAVE_CHANGES")}
          onClick={handleSaveChanges}
          fullWidth
          isLoading={editChildMutation.isLoading}
        />
        <button onClick={handleDeleteChild} className="mx-auto">
          <div className="flex flex-row items-center gap-2">
            <FontAwesomeIcon
              icon={faTrash}
              color={JoonUIColor.semantic.destructive}
            />
            <Typography
              variant="bodySmall"
              color={JoonUIColor.semantic.destructive}
            >
              {t("ECP_DELETE_CHILD")}
            </Typography>
          </div>
        </button>
      </div>
      <DeleteChildModal />
    </SlideUpModal>
  )
}

export default EditChildProfileModal
